import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  type PaginationState,
  type VisibilityState,
} from '@tanstack/react-table';
import { CircleArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  type OutstandingPayment,
  useDashboardOutstandingOverview,
} from '~entities/payment/api/dashboard-outstanding-overview';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import useDebounce from '~shared/hooks/useDebounce';
import { cn } from '~shared/shadcn/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Label } from '~shared/shadcn/ui/label';
import { Progress } from '~shared/shadcn/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const DEFAULT_PAGE_SIZE = 20;

type SortBy = 'daysOverdue' | 'parentName' | 'studentName' | 'dueDate';

export function OutstandingAndOverdue() {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const page = paginationState.pageIndex + 1;
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [sortBy, setSortBy] = useState<SortBy>('daysOverdue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setPaginationState((current) => ({ ...current, pageIndex: 0 }));
  }, [debouncedSearch, sortBy, sortOrder]);

  const { data, isLoading, isError, isFetching } =
    useDashboardOutstandingOverview({
      page,
      limit: paginationState.pageSize,
      sortBy,
      sortOrder,
      q: debouncedSearch.trim() || undefined,
    });
  const { data: currencyDetails } = useGetCurrency();
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const totalCount = pagination?.totalCount ?? 0;
  const hasData = data != null;

  if (isLoading && !hasData) {
    return <div>Loading...</div>;
  }

  if (isError || (!isLoading && !hasData)) {
    return <div>Error loading outstanding overview.</div>;
  }

  return (
    <div className='space-y-6'>
      <Link to='/payment/payment-dashboard' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Outstanding & Overdue</h2>
      </Link>

      <Card>
        <CardHeader className='pb-2'>
          <CardTitle>Total Outstanding Balance</CardTitle>
          <CardDescription>
            Sum of all unpaid invoices (base tuition only)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold'>
            {currencyDetails?.symbol ?? '$'}
            {data?.totalOutstandingBalance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Aging Buckets</CardTitle>
          <CardDescription>
            Outstanding balances by days overdue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {data?.agingBuckets.map((bucket, idx) => (
              <AgingBucket
                key={bucket.label}
                label={bucket.label}
                amount={bucket.amount}
                percentage={bucket.percentage}
                idx={idx}
                currencySymbol={currencyDetails?.symbol ?? '$'}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Outstanding Payments</CardTitle>
          <CardDescription>
            Unpaid invoices (page {page} of {totalPages || 1})
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OutstandingPaymentsTable
            payments={data?.outstandingPayments ?? []}
            currencySymbol={currencyDetails?.symbol ?? '$'}
            isFetching={isFetching}
            pagination={paginationState}
            onPaginationChange={setPaginationState}
            rowCount={totalCount}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AgingBucket({
  label,
  amount,
  percentage,
  idx,
  currencySymbol,
}: {
  label: string;
  amount: number;
  percentage: number;
  idx: number;
  currencySymbol: string;
}) {
  // Color classes for bar and text
  const colorMap: Array<{ bar: string; text: string }> = [
    { bar: 'bg-primary', text: 'text-primary' },
    { bar: 'bg-secondary', text: 'text-secondary' },
    { bar: 'bg-destructive', text: 'text-destructive' },
  ];
  const color = colorMap[idx] ?? { bar: 'bg-primary', text: 'text-primary' };
  const bar = color.bar;
  const text = color.text;

  // For 0% show white/empty bar, for 100% show full color
  const progressBarClass = percentage === 0 ? 'bg-white' : bar;

  return (
    <div className='space-y-2'>
      <div className='flex justify-between'>
        <div className='font-medium'>{label}</div>
        <div className='font-medium'>
          {currencySymbol}
          {amount.toLocaleString()}
        </div>
      </div>
      <Progress
        value={percentage}
        className='bg-muted'
        indicatorClassName={progressBarClass}
      />
      <div className={cn('text-right text-xs', text)}>
        {percentage.toFixed(1)}% of total
      </div>
    </div>
  );
}

/**
 * Formats invoice due dates without off-by-one issues:
 * - `YYYY-MM-DD` is interpreted as a local calendar date (not UTC midnight).
 * - Full ISO strings from the API are shown using the UTC calendar date, matching stored instants.
 */
function formatDueDate(iso: string | null) {
  if (!iso) return '—';
  const trimmed = iso.trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const m = Number(dateOnly[2]);
    const d = Number(dateOnly[3]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return '—';
    }
    const local = new Date(y, m - 1, d);
    return local.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  try {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return '—';
  }
}

const outstandingPaymentColumnHelper = createColumnHelper<OutstandingPayment>();

function OutstandingPaymentsTable({
  payments,
  currencySymbol,
  isFetching,
  pagination,
  onPaginationChange,
  rowCount,
  searchInput,
  setSearchInput,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
}: {
  payments: Array<OutstandingPayment>;
  currencySymbol: string;
  isFetching: boolean;
  pagination: PaginationState;
  onPaginationChange: React.Dispatch<React.SetStateAction<PaginationState>>;
  rowCount: number;
  searchInput: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  sortBy: SortBy;
  setSortBy: React.Dispatch<React.SetStateAction<SortBy>>;
  sortOrder: 'asc' | 'desc';
  setSortOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
}) {
  const columns = useMemo(
    () => [
      outstandingPaymentColumnHelper.accessor('parentName', {
        id: 'parent',
        header: 'Parent',
        enableSorting: false,
        cell: ({ row }) => (
          <ParentNameCell
            name={row.original.parentName}
            parentId={row.original.parentId}
          />
        ),
      }),
      outstandingPaymentColumnHelper.accessor('studentName', {
        id: 'student',
        header: 'Student',
        enableSorting: false,
        cell: ({ row }) => (
          <StudentNameCell
            name={row.original.studentName}
            studentId={row.original.studentId}
          />
        ),
      }),
      outstandingPaymentColumnHelper.accessor('amountDue', {
        id: 'amountDue',
        header: 'Amount due',
        enableSorting: false,
        cell: ({ getValue }) => (
          <>
            {currencySymbol}
            {getValue().toFixed(2)}
          </>
        ),
      }),
      outstandingPaymentColumnHelper.accessor('dueDate', {
        id: 'dueDate',
        header: 'Due date',
        enableSorting: false,
        cell: ({ getValue }) => formatDueDate(getValue()),
      }),
      outstandingPaymentColumnHelper.accessor('daysOverdue', {
        id: 'daysOverdue',
        header: 'Days overdue',
        enableSorting: false,
        cell: ({ getValue }) => {
          const daysOverdue = getValue();
          return (
            <div className='flex items-center justify-end md:justify-start'>
              <div
                className={cn(
                  'mr-2 size-2 rounded-full',
                  daysOverdue <= 15
                    ? 'bg-blue-500'
                    : daysOverdue <= 30
                      ? 'bg-yellow-500'
                      : 'bg-red-500',
                )}
              />
              {daysOverdue} days
            </div>
          );
        },
      }),
      outstandingPaymentColumnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ getValue }) => <div className='capitalize'>{getValue()}</div>,
      }),
    ],
    [currencySymbol],
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  return (
    <EnrollioTable
      ariaLabel='Outstanding payments'
      totalLabel='Outstanding payments'
      filterHeader={
        <>
          <EnrollioTableSearch
            placeholder='Search parents or students...'
            value={searchInput}
            onChange={setSearchInput}
          />
          <div className='space-y-2'>
            <Label htmlFor='outstanding-sort-by'>Sort by</Label>
            <Select
              value={sortBy}
              onValueChange={(value) => {
                setSortBy(value as SortBy);
              }}
            >
              <SelectTrigger
                id='outstanding-sort-by'
                className='w-full sm:w-[200px]'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='daysOverdue'>Days overdue</SelectItem>
                <SelectItem value='parentName'>Parent name</SelectItem>
                <SelectItem value='studentName'>Student name</SelectItem>
                <SelectItem value='dueDate'>Due date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-2'>
            <Label htmlFor='outstanding-sort-order'>Order</Label>
            <Select
              value={sortOrder}
              onValueChange={(value) => {
                setSortOrder(value as 'asc' | 'desc');
              }}
            >
              <SelectTrigger
                id='outstanding-sort-order'
                className='w-full sm:w-[140px]'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='desc'>Descending</SelectItem>
                <SelectItem value='asc'>Ascending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      }
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={columns}
      data={payments}
      getRowId={(row, index) => row.invoiceId ?? String(index)}
      isUpdating={isFetching}
      manualPagination
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      rowCount={rowCount}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
    />
  );
}

function ParentNameCell({
  name,
  parentId,
}: {
  name: string;
  parentId: string | null;
}) {
  const isPlaceholder = !name || name === '—' || name === 'N/A';
  if (isPlaceholder) {
    return name || '—';
  }
  if (parentId) {
    return (
      <Link
        to='/parents/transactions-table'
        search={{ parentId }}
        className='text-black underline hover:opacity-80'
      >
        {name}
      </Link>
    );
  }
  return name;
}

function StudentNameCell({
  name,
  studentId,
}: {
  name: string;
  studentId: string | null;
}) {
  const isPlaceholder = !name || name === '—' || name === 'N/A';
  if (isPlaceholder) {
    return name || '—';
  }
  if (studentId) {
    return (
      <Link
        to='/students/$studentId/details'
        params={{ studentId }}
        className='text-black underline hover:opacity-80'
      >
        {name}
      </Link>
    );
  }
  return name;
}
